/**
 * The blog's one markdown renderer.
 *
 * Posts are stored as markdown and rendered ONCE, on save, by this module;
 * the public pages serve the stored HTML verbatim. The admin console imports
 * this same file (by relative path) for its live preview, so what an editor
 * sees is byte-for-byte what readers get. That is why the module is pure:
 * no Convex imports, no Node builtins, nothing that would not run in a
 * browser.
 *
 * Safety model: `html: false` makes markdown-it escape every raw HTML tag in
 * the source, and its built-in `validateLink` refuses `javascript:`,
 * `vbscript:`, `file:` and non-image `data:` URLs, so a link or image with
 * one of those schemes is left as literal text. Every attribute we add is
 * rendered through markdown-it's own escaping. The stored HTML is therefore
 * safe to inject without a second sanitiser.
 *
 * What it adds on top of stock markdown-it:
 *   - `#` headings in the body are demoted to `<h2>`: the post title is the
 *     page's only `<h1>`. `demotedH1` reports that it happened so the editor
 *     can say so.
 *   - Every heading gets a stable, unique `id`, and h2/h3 feed the TOC.
 *   - Links leaving wydm.app open in a new tab with `rel="noopener"`.
 *   - Images are lazy-loaded; an image alone in its paragraph becomes a
 *     `<figure>`, with its markdown title (`![alt](src "caption")`) as the
 *     caption.
 *   - An image whose intrinsic size the caller knows (the `imageDims` map,
 *     keyed by the URL exactly as written in the markdown) gets `width` and
 *     `height` attributes, so the browser reserves its box before the bytes
 *     arrive. An unknown image gets neither; a guessed size would be worse.
 *     A sized image with a downscaled variant also gets `srcset`/`sizes`.
 */

import MarkdownIt from "markdown-it";
import type { Token } from "markdown-it";

export interface TocEntry {
  level: 2 | 3;
  text: string;
  id: string;
}

/**
 * Intrinsic pixel size of an image, keyed by its public URL, plus its
 * downscaled copy when one exists.
 */
export type ImageDimsMap = ReadonlyMap<
  string,
  { width: number; height: number; variantUrl?: string; variantWidth?: number }
>;

interface RenderEnv {
  toc: TocEntry[];
  demotedH1: boolean;
  imageDims?: ImageDimsMap;
}

export interface RenderedPost {
  html: string;
  toc: TocEntry[];
  /** Plain text of the body: feeds search and the reading-time estimate. */
  text: string;
  readingMinutes: number;
  /** True when at least one `#` heading was demoted to `<h2>`. */
  demotedH1: boolean;
}

/** Average adult silent-reading speed for web prose. */
const WORDS_PER_MINUTE = 220;

/**
 * Lowercase ASCII slug: diacritics folded, anything else collapsed to single
 * dashes, no leading or trailing dash. Used for heading ids here, and by the
 * backend and the editor for post, pillar and tag slugs, so all three agree.
 */
export function slugify(input: string): string {
  return input
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const SITE_HOSTS = /^(?:[a-z0-9-]+\.)*wydm\.app$/i;

function isExternal(href: string): boolean {
  const m = /^(?:https?:)?\/\/([^/?#:]+)/i.exec(href);
  if (!m) return false;
  return !SITE_HOSTS.test(m[1]);
}

function inlineText(children: Token[] | null): string {
  if (!children) return "";
  let out = "";
  for (const c of children) {
    if (c.type === "text" || c.type === "code_inline") out += c.content;
    else if (c.type === "softbreak" || c.type === "hardbreak") out += " ";
    else if (c.type === "image") out += inlineText(c.children);
  }
  return out;
}

/** Post images fill the viewport on phones and a 768px column above that. */
export const POST_IMAGE_SIZES = "(max-width: 768px) 100vw, 768px";

function isPixelSize(n: number): boolean {
  return Number.isInteger(n) && n > 0;
}

function createRenderer(): MarkdownIt {
  const md = new MarkdownIt({
    html: false,
    linkify: true,
    typographer: true,
  });
  const escapeHtml = md.utils.escapeHtml;

  md.core.ruler.push("wydm_blog", (state) => {
    const tokens = state.tokens;
    const env = state.env as RenderEnv;
    const used = new Map<string, number>();

    for (let i = 0; i < tokens.length; i++) {
      const tok = tokens[i];

      if (tok.type === "heading_open") {
        const close = tokens[i + 2];
        if (tok.tag === "h1") {
          tok.tag = "h2";
          tok.markup = "##";
          if (close?.type === "heading_close") {
            close.tag = "h2";
            close.markup = "##";
          }
          env.demotedH1 = true;
        }
        const text = inlineText(tokens[i + 1]?.children ?? null).trim();
        const base = slugify(text) || "section";
        const seen = used.get(base) ?? 0;
        used.set(base, seen + 1);
        const id = seen === 0 ? base : `${base}-${seen + 1}`;
        tok.attrSet("id", id);
        const level = Number(tok.tag.slice(1));
        if ((level === 2 || level === 3) && text) {
          env.toc.push({ level, text, id });
        }
        continue;
      }

      if (tok.type === "inline" && tok.children) {
        for (const c of tok.children) {
          if (c.type === "link_open" && isExternal(c.attrGet("href") ?? "")) {
            c.attrSet("target", "_blank");
            c.attrSet("rel", "noopener");
          }
        }
        // An image alone in its paragraph (whitespace aside) is a figure.
        // The paragraph wrapper is hidden so we never emit <p><figure>.
        const open = tokens[i - 1];
        const close = tokens[i + 1];
        if (open?.type === "paragraph_open" && close?.type === "paragraph_close") {
          const meaningful = tok.children.filter(
            (c) =>
              !(c.type === "text" && c.content.trim() === "") &&
              c.type !== "softbreak",
          );
          if (meaningful.length === 1 && meaningful[0].type === "image") {
            meaningful[0].meta = { ...(meaningful[0].meta ?? {}), figure: true };
            open.hidden = true;
            close.hidden = true;
          }
        }
      }
    }
  });

  md.renderer.rules.image = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const alt = self.renderInlineAsText(token.children ?? [], options, env);
    const src = token.attrGet("src") ?? "";
    const title = token.attrGet("title");
    const dims = (env as RenderEnv).imageDims?.get(src);
    const sized = dims && isPixelSize(dims.width) && isPixelSize(dims.height) ? dims : undefined;
    const size = sized ? `width="${sized.width}" height="${sized.height}" ` : "";
    const srcset =
      sized?.variantUrl && sized.variantWidth && isPixelSize(sized.variantWidth)
        ? `srcset="${escapeHtml(src)} ${sized.width}w, ${escapeHtml(sized.variantUrl)} ${sized.variantWidth}w" ` +
          `sizes="${POST_IMAGE_SIZES}" `
        : "";
    const img =
      `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" ` +
      `${size}${srcset}loading="lazy" decoding="async">`;
    if (token.meta?.figure) {
      const caption = title ? `<figcaption>${escapeHtml(title)}</figcaption>` : "";
      return `<figure>${img}${caption}</figure>\n`;
    }
    return img;
  };

  return md;
}

let renderer: MarkdownIt | null = null;

function plainText(tokens: Token[]): string {
  const parts: string[] = [];
  for (const t of tokens) {
    if (t.type === "inline") parts.push(inlineText(t.children));
    else if (t.type === "fence" || t.type === "code_block") parts.push(t.content);
  }
  return parts
    .map((p) => p.trim())
    .filter(Boolean)
    .join("\n");
}

export function renderPost(markdown: string, imageDims?: ImageDimsMap): RenderedPost {
  renderer ??= createRenderer();
  const env: RenderEnv = { toc: [], demotedH1: false, imageDims };
  const tokens = renderer.parse(markdown, env);
  const html = renderer.renderer.render(tokens, renderer.options, env);
  const text = plainText(tokens);
  const words = text.split(/\s+/).filter(Boolean).length;
  return {
    html,
    toc: env.toc,
    text,
    readingMinutes: Math.max(1, Math.ceil(words / WORDS_PER_MINUTE)),
    demotedH1: env.demotedH1,
  };
}
